import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { FilterOption } from '@/components/ui/FilterSelect/FilterSelect';
import { useFilterTags } from '@/hooks/useFilterTags';
import useToast, { TOAST_LABELS } from '@/hooks/useToast';
import { useAssignAgentToCustomersMutation, useGetAgentsQuery } from '@/store/rest/agentsApi';
import { useGetCustomerQuery, useGetCustomersQuery } from '@/store/rest/customersApi';
import { TAgent, TAssignedCustomers, TRunAgentParams } from '@/types/agents';
import { ICustomer } from '@/types/customers';
import { AGENTS_PAGE_FILTER_NAMES, mapFiltersToApiParams } from '@/utils/filterUtils';

const useAgentsPage = () => {
  const searchParams = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');
  const { showSuccess, fromException } = useToast();

  const [selectedAgent, setSelectedAgent] = useState<TAgent | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string>('');
  const [searchCustomers, setSearchCustomers] = useState<string>('');
  const [searchAssignCustomers, setSearchAssignCustomers] = useState<string>('');
  const [assignedCustomers, setAssignedCustomers] = useState<TAssignedCustomers[]>([]);
  const [agentRunData, setAgentRunData] = useState<TRunAgentParams | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const {
    filters,
    updateFilter,
    removeFilter,
    clearAllFilters,
    createFilterTags
  } = useFilterTags({ filterNames: AGENTS_PAGE_FILTER_NAMES });

  const selectedCustomers = filters.customer;

  const { data: agentsData, isLoading: isLoadingAgents, isFetching: isFetchingAgents, refetch: refetchAgents } = useGetAgentsQuery({
    limit: 9,
    page,
    search,
    ...mapFiltersToApiParams(selectedCustomers, 'customerIds'),
  });

  const { data: customersData, isLoading: isLoadingCustomers, isFetching: isFetchingCustomers } = useGetCustomersQuery(
    {
      page: 1,
      limit: 10,
      search: searchCustomers,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: allCustomersData, isLoading: isLoadingAllCustomers } = useGetCustomersQuery(
    {
      page: 1,
      limit: 1000,
      search: searchAssignCustomers,
    },
    {
      skip: !isAssignModalOpen,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: customerFromUrl } = useGetCustomerQuery(
    { referenceId: customerIdFromUrl ?? '' },
    { skip: !customerIdFromUrl }
  );

  const [assignAgentToCustomers, { isLoading: isLoadingAssignAgentToCustomers }] = useAssignAgentToCustomersMutation();

  const customerOptions = customersData?.items.map((customer: ICustomer ) => ({
    value: customer.referenceId,
    label: customer.name,
  })) || [];

  const handleAgentEdit = (agent: TAgent) => {
    setSelectedAgent(agent);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedAgent(null);
  };

  const handleRemoveCustomer = (customerId: string) => {
    if (!selectedAgent) {return;}
    const newAssignedCustomers = assignedCustomers.filter((customer) => customer.customerId !== customerId);
    setAssignedCustomers(newAssignedCustomers);
  };

  const handleAssignMoreCustomers = () => {
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSearchAssignCustomers('');
  };

  const handleSaveAssignments = (selectedCustomerIds: string[]) => {
    if (!selectedAgent) {
      return;
    }

    const newAssignedCustomers = selectedCustomerIds.map((customerId) => ({
      customerId,
      customerName: allCustomersData?.items.find((customer: ICustomer) => customer.referenceId === customerId)?.name || '',
      agentId: selectedAgent.referenceId,
      agentName: selectedAgent.name,
    }));

    setAssignedCustomers(newAssignedCustomers);
    handleCloseAssignModal();
  };

  const handleSave = async () => {
    if (!selectedAgent) {
      return;
    }
    try {
      await assignAgentToCustomers({
        agentId: selectedAgent.referenceId,
        customerIds: assignedCustomers.map((customer) => customer.customerId),
      });
      showSuccess({ label: TOAST_LABELS.updateSuccess, message: 'Agent assigned to customers successfully' });
      refetchAgents();
      handleCloseSidebar();
    } catch (error) {
      fromException({ error, label: TOAST_LABELS.updateError });
    }
  };

  const handleRunAgent = (params: TRunAgentParams) => {
    setAgentRunData(params);
    setIsExecuting(true);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleCustomerChange = (customers: FilterOption[]) => {
    updateFilter('customer', customers);
    setPage(1);
  };

  const handleSearchCustomers = (value: string) => {
    setSearchCustomers(value);
  };

  const handleRemoveFilter = (filterId: string) => {
    removeFilter(filterId);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
    setPage(1);
  };

  useEffect(() => {
    if (customerIdFromUrl && customerFromUrl && selectedCustomers.length === 0) {
      updateFilter('customer', [{ value: customerFromUrl.referenceId, label: customerFromUrl.name }]);
    }
  }, [customerIdFromUrl, customerFromUrl]);

  useEffect(() => {
    if (selectedAgent) {
      setAssignedCustomers(selectedAgent.assignCustomers);
    } else {
      setAssignedCustomers([]);
    }
  }, [selectedAgent]);

  return {
    agentsData,
    selectedAgent,
    isSidebarOpen,
    isAssignModalOpen,
    page,
    search,
    searchCustomers,
    selectedCustomers,
    customerOptions,
    assignedCustomers,
    allCustomers: allCustomersData?.items || [],
    isLoadingAllCustomers,
    handleAgentEdit,
    handleCloseSidebar,
    handleRemoveCustomer,
    handleAssignMoreCustomers,
    handleCloseAssignModal,
    handleSaveAssignments,
    handleSave,
    handleRunAgent,
    setPage,
    handleSearch,
    handleCustomerChange,
    handleSearchCustomers,
    handleRemoveFilter,
    handleClearAllFilters,
    createFilterTags,
    isLoadingAgents: isLoadingAgents || isFetchingAgents,
    isLoadingCustomers: isLoadingCustomers || isFetchingCustomers,
    isLoadingAssignAgentToCustomers,
    isExecuting,
    setIsExecuting,
    agentRunData,
  };
};

export default useAgentsPage;